/**
 * Paletas de color por bioma — datos puros (sin Phaser), consumidos por las
 * escenas/vistas (`GameScene`, `PlinkoView`/`Board`, `PlayPanel`) para teñir
 * fondo y props de la franja de combate, el tablero de vertido y la isla del
 * lobby según el bioma del capítulo actual (`ChapterDef.biome`).
 *
 * Un mismo bioma (p.ej. "cripta") se ve igual en todo el juego: un solo
 * lugar define sus colores. Bioma desconocido cae a "bosque" por defecto.
 */

export interface BiomePalette {
  id: string;
  /** Nombre legible en español, para etiquetas de UI. */
  label: string;
  /** Cielo/fondo de la franja de combate (degradado de arriba a abajo). */
  skyTop: number;
  skyBottom: number;
  /** Suelo de la franja de combate. */
  groundTop: number;
  groundBottom: number;
  /** Fondo del tablero de vertido (degradado de arriba a abajo). */
  boardTop: number;
  boardBottom: number;
  /** Color de acento del bioma (líneas del embudo, vaso receptor, detalles). */
  accent: number;
  /** Colores de las siluetas decorativas (props) de la franja de combate. */
  propPrimary: number;
  propSecondary: number;
}

export const BIOME_PALETTES: Record<string, BiomePalette> = {
  bosque: {
    id: 'bosque',
    label: 'Bosque Otoñal',
    skyTop: 0x6b4a2e,
    skyBottom: 0x2c1f45,
    groundTop: 0x3a2410,
    groundBottom: 0x241705,
    boardTop: 0x2a1a12,
    boardBottom: 0x140c08,
    accent: 0xe0824d,
    propPrimary: 0x5c3a22,
    propSecondary: 0xd97a3f,
  },
  desierto: {
    id: 'desierto',
    label: 'Lejano Oeste',
    skyTop: 0xc97b3d,
    skyBottom: 0x4a2f2b,
    groundTop: 0x8a6a3a,
    groundBottom: 0x5c4626,
    boardTop: 0x3a2c18,
    boardBottom: 0x1e160c,
    accent: 0xe0b34d,
    propPrimary: 0x3f7a4a,
    propSecondary: 0xa0623a,
  },
  cripta: {
    id: 'cripta',
    label: 'Cripta Sombría',
    skyTop: 0x1f2a3a,
    skyBottom: 0x0d1420,
    groundTop: 0x232a3a,
    groundBottom: 0x11151f,
    boardTop: 0x131a26,
    boardBottom: 0x090c12,
    accent: 0x7fe0c8,
    propPrimary: 0x5a5a6e,
    propSecondary: 0x3f6b52,
  },
  volcan: {
    id: 'volcan',
    label: 'Volcán Ardiente',
    skyTop: 0x3a1414,
    skyBottom: 0x1a0a0a,
    groundTop: 0x241414,
    groundBottom: 0x120a0a,
    boardTop: 0x2a1210,
    boardBottom: 0x140806,
    accent: 0xff7043,
    propPrimary: 0x3a2020,
    propSecondary: 0xff8c42,
  },
};

const DEFAULT_BIOME = 'bosque';

/** Paleta del bioma dado; cae a "bosque" si el id no está mapeado. */
export function biomePaletteFor(biome: string): BiomePalette {
  return BIOME_PALETTES[biome] ?? (BIOME_PALETTES[DEFAULT_BIOME] as BiomePalette);
}

/** Nombre legible del bioma (cae al valor crudo si no está mapeado). */
export function biomeLabel(biome: string): string {
  return BIOME_PALETTES[biome]?.label ?? biome;
}
